import type { FlashbarProps } from "@cloudscape-design/components/flashbar";
import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from "react";

export type FlashType = "success" | "error" | "warning" | "info";

interface FlashInput {
  type: FlashType;
  header: string;
  content?: string;
}

interface FlashContextValue {
  items: FlashbarProps.MessageDefinition[];
  publish: (flash: FlashInput) => void;
  clear: () => void;
}

const FlashContext = createContext<FlashContextValue | null>(null);

let nextId = 0;

/**
 * Thay cho toast tự chế của bản trước: flash message dismiss được, đọc được
 * bằng screen reader và nằm ở slot notifications của AppLayout.
 */
export function FlashProvider({ children }: PropsWithChildren) {
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const publish = useCallback((flash: FlashInput) => {
    const id = `flash-${nextId++}`;
    setItems((previous) => [
      ...previous,
      {
        id,
        type: flash.type,
        header: flash.header,
        content: flash.content,
        dismissible: true,
        dismissLabel: "Đóng thông báo",
        onDismiss: () => setItems((current) => current.filter((item) => item.id !== id)),
      },
    ]);
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ items, publish, clear }), [items, publish, clear]);
  return <FlashContext.Provider value={value}>{children}</FlashContext.Provider>;
}

export function useFlash(): FlashContextValue {
  const value = useContext(FlashContext);
  if (!value) throw new Error("useFlash phải nằm trong FlashProvider");
  return value;
}
